import CoreUtils from '../common/utils/CoreUtils';

import PreferenceManager from '../application/PreferenceManager';
import PreferenceApi from '../api-services/PreferenceApi';

export default class ScheduleColumnSettings {
  constructor(preferenceGroupName) {
    this.preferenceGroupName = preferenceGroupName;

    this.filtering = {};
    this.grouping = {};
    this.sorting = {};
    this.sizing = {};
    this.visibility = {};

    this.defaultVisibility = {};

    this.preferenceNames = [
      PreferenceManager.ColumnFiltering,
      PreferenceManager.ColumnGrouping,
      PreferenceManager.ColumnSorting,
      PreferenceManager.ColumnSizing,
      PreferenceManager.ColumnVisibility
    ];
  }

  registerDefaultSettings(column) {
    this.defaultVisibility[column.dataField] = column.visible;
  }

  serializeColumnSettings() {
    return [{
      group: this.preferenceGroupName,
      name: PreferenceManager.ColumnFiltering,
      value: this.filtering
    }, {
      group: this.preferenceGroupName,
      name: PreferenceManager.ColumnGrouping,
      value: this.grouping
    }, {
      group: this.preferenceGroupName,
      name: PreferenceManager.ColumnSorting,
      value: this.sorting
    }, {
      group: this.preferenceGroupName,
      name: PreferenceManager.ColumnSizing,
      value: this.sizing
    }, {
      group: this.preferenceGroupName,
      name: PreferenceManager.ColumnVisibility,
      value: this.visibility
    }];
  }

  loadColumnSettings() {
    this.loadColumnSetting(
      this.preferenceGroupName,
      PreferenceManager.ColumnFiltering,
      value => this.filtering = value
    );

    this.loadColumnSetting(
      this.preferenceGroupName,
      PreferenceManager.ColumnGrouping,
      value => this.grouping = value
    );

    this.loadColumnSetting(
      this.preferenceGroupName,
      PreferenceManager.ColumnSorting,
      value => this.sorting = value
    );

    this.loadColumnSetting(
      this.preferenceGroupName,
      PreferenceManager.ColumnSizing,
      value => this.sizing = value
    );

    this.loadColumnSetting(
      this.preferenceGroupName,
      PreferenceManager.ColumnVisibility,
      value => this.visibility = value
    );
  }

  loadColumnSetting(group, name, loadSettingFunc) {
    var value = PreferenceManager.retrieveValue(group, name);
    if (value !== undefined)
      loadSettingFunc(value);
  }

  storeColumnSettings() {
    PreferenceManager.updateValues(this.serializeColumnSettings());
  }

  enactColumnSettings(column) {
    this.registerDefaultSettings(column);

    if (this.filtering !== undefined)
      this.enactFilterValuesSetting(column);

    if (this.grouping !== undefined)
      this.enactGroupIndexSetting(column);

    if (this.sorting !== undefined)
      this.enactSortIndexSetting(column);

    if (this.sizing !== undefined)
      this.enactWidthSetting(column);

    if (this.visibility !== undefined)
      this.enactVisibleSetting(column);
  }

  retrieveColumnSetting(dataGrid, columnIndex, dataField, value, previousValue) {
    var column = dataGrid.columnOption(columnIndex);

    switch (dataField) {
      case 'filterValues':
        this.retrieveFilterValuesSetting(column, value);
        break;
      case 'groupIndex':
        this.retrieveGroupIndexSetting(column, value);
        break;
      case 'sortIndex':
        this.retrieveSortIndexSetting(column, value);
        break;
      case 'sortOrder':
        this.retrieveSortOrderSetting(column, value);
        break;
      case 'width':
        this.retrieveWidthSetting(column, value);
        break;
      case 'visible':
        this.retrieveVisibleSetting(column, value);
        break;
      default:
        break;
    }
  }

  setDefaultPreferences(onOperationComplete) {
    PreferenceApi.setDefaultPreferences(this.serializeColumnSettings(), onOperationComplete);
  }

  clearDefaultPreferences(onOperationComplete) {
    PreferenceApi.deleteDefaultPreferences(this.preferenceGroupName, this.preferenceNames, onOperationComplete);
  }

  resetAllColumnPreferences(onOperationComplete) {
    PreferenceApi.deletePreferencesForAllUsers(this.preferenceGroupName, this.preferenceNames, onOperationComplete);
  }

  enactFilterValuesSetting(column) {
    if (CoreUtils.isUndefinedOrNull(this.filtering[column.dataField]))
      return;
      
    column.filterType = this.filtering[column.dataField].filterType;
    column.filterValues = this.filtering[column.dataField].filterValues;
  }

  retrieveFilterValuesSetting(column, value) {
    if (CoreUtils.isDefinedAndNotNull(value))
      this.filtering[column.dataField] = {
        filterType: column.filterType,
        filterValues: column.filterValues
      };
    else
      delete this.filtering[column.dataField];
  }

  enactGroupIndexSetting(column) {
    if (CoreUtils.isUndefinedOrNull(this.grouping[column.dataField]))
      return;
    
    column.groupIndex = this.grouping[column.dataField].groupIndex;
    column.sortOrder = this.grouping[column.dataField].sortOrder;
  }

  retrieveGroupIndexSetting(column, value) {
    if (value !== undefined) {
      this.grouping[column.dataField] = {
        groupIndex: column.groupIndex,
        sortOrder: column.sortOrder
      };

      delete this.sorting[column.dataField];
      return;
    }

    delete this.grouping[column.dataField];

    if (column.sortIndex !== undefined)
      this.sorting[column.dataField] = {
        sortIndex: column.sortIndex,
        sortOrder: column.sortOrder
      };
  }

  enactSortIndexSetting(column) {
    if (CoreUtils.isUndefinedOrNull(this.sorting[column.dataField]))
      return;

    column.sortIndex = this.sorting[column.dataField].sortIndex;
    column.sortOrder = this.sorting[column.dataField].sortOrder;
  }

  retrieveSortIndexSetting(column, value) {
    if (value !== undefined) {
      this.sorting[column.dataField] = {
        sortIndex: column.sortIndex,
        sortOrder: column.sortOrder
      };

      delete this.grouping[column.dataField];
      return;
    }

    delete this.sorting[column.dataField];
  }

  retrieveSortOrderSetting(column, value) {
    if (CoreUtils.isDefinedAndNotNull(this.grouping[column.dataField]))
      this.grouping[column.dataField] = {
        groupIndex: column.groupIndex,
        sortOrder: column.sortOrder
      };
    else if (value !== undefined)
      this.sorting[column.dataField] = {
        sortIndex: column.sortIndex,
        sortOrder: column.sortOrder
      };
    else
      delete this.sorting[column.dataField];
  }

  enactWidthSetting(column) {
    if (CoreUtils.isUndefinedOrNull(this.sizing[column.dataField]))
      return;

    column.width = this.sizing[column.dataField].width;
  }

  retrieveWidthSetting(column, value) {
    if (column.allowResizing && value !== undefined)
      this.sizing[column.dataField] = {
        width: column.width
      }
    else
      delete this.sizing[column.dataField];
  }

  enactVisibleSetting(column) {
    if (CoreUtils.isUndefinedOrNull(this.visibility[column.dataField]))
      return;

    column.visible = this.visibility[column.dataField].visible;
  }

  retrieveVisibleSetting(column, value) {
    if (column.visible !== this.defaultVisibility[column.dataField])
      this.visibility[column.dataField] = {
        visible: column.visible
      };
    else
      delete this.visibility[column.dataField];
  }
}